import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider,
  LinearProgress,
  Rating,
  CircularProgress,
} from '@mui/material'
import { useGetHelpCenterQuery } from '@store/api/helpApi'
import type { HelpArticle, HelpCenterData } from '@store/api/helpApi'
import { HELP_CENTER_FALLBACK } from '@/content/helpCenterFallback'
import { loadHelpBookmarks, saveHelpBookmarks } from '@/content/helpCenterBookmarks'
import {
  HELP_BASELINE_ITEM_COUNT,
  articleMatchesUiCategory,
  faqMatchesUiCategory,
} from '@content/helpCenterDefaults'
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  PlayCircleOutline as VideoIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Help as HelpIcon,
  LibraryBooks as GuidesIcon,
  BugReport as TroubleshootIcon,
  QuestionAnswer as FaqIcon,
  Lightbulb as TipIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

const CATEGORIES = [
  { label: 'Getting Started', icon: <HelpIcon />, color: '#4CAF50' },
  { label: 'Feature Guides', icon: <GuidesIcon />, color: '#2196F3' },
  { label: 'Troubleshooting', icon: <TroubleshootIcon />, color: '#FF9800' },
  { label: 'FAQ', icon: <FaqIcon />, color: '#9C27B0' },
]

const HelpCenter: React.FC = () => {
  const { data, isLoading, isError } = useGetHelpCenterQuery()
  const contentReady = Boolean(data) || isError || !isLoading
  const { articles: helpArticles, faqs: helpFaqs, contextualTips, source: helpSource } = useMemo(() => {
    if (!contentReady) {
      return { articles: [], faqs: [], contextualTips: [], source: undefined as HelpCenterData['source'] }
    }
    const help = data ?? HELP_CENTER_FALLBACK
    return {
      articles: help.articles,
      faqs: help.faqs,
      contextualTips: help.contextualTips,
      source: help.source,
    }
  }, [contentReady, data])

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false)
  const [bookmarks, setBookmarks] = useState<string[]>([])
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, 'up' | 'down'>>({})
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [tipVisible, setTipVisible] = useState(true)

  useEffect(() => {
    if (!contentReady || helpArticles.length === 0) return
    const knownSlugs = new Set(helpArticles.map((a) => a.slug))
    setBookmarks((prev) => {
      const loaded = loadHelpBookmarks(knownSlugs)
      if (prev.length === 0 && loaded.length > 0) return loaded
      return prev.filter((s) => knownSlugs.has(s))
    })
  }, [contentReady, helpArticles])

  const tipIndex = useMemo(
    () => Math.floor(Math.random() * Math.max(contextualTips.length, 1)),
    [contextualTips.length]
  )

  const docCoveragePercent = Math.min(
    100,
    Math.round(((helpArticles.length + helpFaqs.length) / HELP_BASELINE_ITEM_COUNT) * 100)
  )

  const filteredArticles = useMemo(() => {
    return helpArticles.filter((article) => {
      const matchesSearch =
        !searchTerm ||
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
      const matchesCategory = articleMatchesUiCategory(article.category, selectedCategory)
      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory, helpArticles])

  const filteredFaqs = useMemo(() => {
    const lower = searchTerm.toLowerCase()
    return helpFaqs.filter((faq) => {
      const matchesSearch =
        !searchTerm ||
        faq.question.toLowerCase().includes(lower) ||
        faq.answer.toLowerCase().includes(lower)
      const matchesCategory = faqMatchesUiCategory(faq.category, selectedCategory)
      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory, helpFaqs])

  const showFaqSection = filteredFaqs.length > 0

  const toggleBookmark = useCallback((slug: string) => {
    setBookmarks((prev) => {
      const next = prev.includes(slug) ? prev.filter((b) => b !== slug) : [...prev, slug]
      saveHelpBookmarks(next)
      setSnackbar({
        open: true,
        message: prev.includes(slug) ? 'Article removed from bookmarks' : 'Article bookmarked!',
      })
      return next
    })
  }, [])

  const handleFeedback = useCallback((articleId: string, vote: 'up' | 'down') => {
    setFeedbackGiven((prev) => ({ ...prev, [articleId]: vote }))
    setSnackbar({ open: true, message: 'Thanks for your feedback!' })
  }, [])

  const bookmarkedArticles = useMemo(
    () => helpArticles.filter((a) => bookmarks.includes(a.slug)),
    [bookmarks, helpArticles]
  )

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.5rem' } }}
          >
            Help Center
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Find guides, tutorials, and answers to common questions.
          </Typography>
          <TextField
            fullWidth
            placeholder="Search help articles, guides, and FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{ 'aria-label': 'Search help content' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 640,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)' },
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {!contentReady ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress aria-label="Loading help content" />
          </Box>
        ) : (
          <>
        {isError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not refresh help content from the server. Showing built-in help articles.
          </Alert>
        )}

        {/* Contextual Tip */}
        {tipVisible && contextualTips.length > 0 && (
          <Paper
            role="note"
            aria-label="contextual tip"
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#FFF8E1',
              borderLeft: '4px solid #DAA520',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TipIcon sx={{ color: '#DAA520', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {contextualTips[tipIndex % contextualTips.length]}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setTipVisible(false)}
              aria-label="dismiss tip"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        )}

        {/* Category Filter */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Browse by Category
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="All Topics"
              onClick={() => setSelectedCategory(null)}
              color={selectedCategory === null ? 'primary' : 'default'}
              variant={selectedCategory === null ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat.label}
                icon={React.cloneElement(cat.icon as React.ReactElement, { fontSize: 'small' })}
                label={cat.label}
                onClick={() => setSelectedCategory(cat.label === selectedCategory ? null : cat.label)}
                color={selectedCategory === cat.label ? 'primary' : 'default'}
                variant={selectedCategory === cat.label ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Bookmarked Articles */}
        {!searchTerm && !selectedCategory && bookmarkedArticles.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#8B4513' }}>
              <BookmarkIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Your Saved Articles
            </Typography>
            <Grid container spacing={2}>
              {bookmarkedArticles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={`bookmark-${article.id}`}>
                  <ArticleCard
                    article={article}
                    isBookmarked={true}
                    onToggleBookmark={toggleBookmark}
                    feedbackGiven={feedbackGiven[article.id]}
                    onFeedback={handleFeedback}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ mt: 3 }} />
          </Box>
        )}

        {/* Search Results Count */}
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} and{' '}
            {filteredFaqs.length} FAQ{filteredFaqs.length !== 1 ? 's' : ''} for &quot;{searchTerm}&quot;
          </Typography>
        )}

        {/* Video Tutorials Section */}
        {(!selectedCategory || selectedCategory === 'Feature Guides') && !searchTerm && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Video Tutorials
            </Typography>
            <Grid container spacing={2}>
              {helpArticles.filter((a) => a.isVideo).map((article) => (
                <Grid item xs={12} sm={6} md={3} key={article.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 },
                      transition: 'box-shadow 0.2s',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#1a1a1a',
                        height: 120,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                      role="img"
                      aria-label={`Video thumbnail for ${article.title}`}
                    >
                      <VideoIcon sx={{ fontSize: 48, color: '#DAA520' }} />
                      <Chip
                        label={article.videoDuration}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {article.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.category}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Articles Grid */}
        {filteredArticles.filter((a) => !a.isVideo || searchTerm).length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {searchTerm ? 'Search Results' : selectedCategory || 'All Articles'}
            </Typography>
            <Grid container spacing={2}>
              {filteredArticles
                .filter((a) => !a.isVideo || searchTerm)
                .map((article) => (
                  <Grid item xs={12} sm={6} md={4} key={article.id}>
                    <ArticleCard
                      article={article}
                      isBookmarked={bookmarks.includes(article.slug)}
                      onToggleBookmark={toggleBookmark}
                      feedbackGiven={feedbackGiven[article.id]}
                      onFeedback={handleFeedback}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        {/* FAQ Section */}
        {showFaqSection && filteredFaqs.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Frequently Asked Questions
            </Typography>
            {filteredFaqs.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expandedFaq === faq.id}
                onChange={(_e, isExpanded) => setExpandedFaq(isExpanded ? faq.id : false)}
                sx={{ mb: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    '&:hover': { backgroundColor: '#e9ecef' },
                    '&.Mui-expanded': { backgroundColor: '#e9ecef' },
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                    <Chip
                      label={faq.category}
                      size="small"
                      sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Empty state */}
        {filteredArticles.length === 0 && filteredFaqs.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <HelpIcon sx={{ fontSize: 60, color: '#8B4513', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No results for &quot;{searchTerm}&quot;
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try different keywords or browse by category above.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory(null)
              }}
            >
              Clear Search
            </Button>
          </Paper>
        )}

        {/* Documentation Progress Indicator */}
        <Paper sx={{ p: 3, mt: 2, backgroundColor: '#F5F5DC' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Documentation Coverage
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {helpArticles.length} articles &bull; {helpFaqs.length} FAQs &bull;{' '}
            {helpArticles.filter((a) => a.isVideo).length} video tutorials
            {helpSource && (helpSource === 'merged' || helpSource === 'database') ? ' (from CMS)' : ''}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={docCoveragePercent}
            sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: '#e0d0b0' }}
          />
          <Typography variant="caption" color="text.secondary">
            Documentation is {docCoveragePercent}% complete — edit articles in Admin &gt; Content CMS.
          </Typography>
        </Paper>
          </>
        )}
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

interface ArticleCardProps {
  article: HelpArticle
  isBookmarked: boolean
  onToggleBookmark: (id: string) => void
  feedbackGiven?: 'up' | 'down'
  onFeedback: (id: string, vote: 'up' | 'down') => void
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  isBookmarked,
  onToggleBookmark,
  feedbackGiven,
  onFeedback,
}) => {
  const tipColorMap: Record<string, string> = {
    info: '#E3F2FD',
    warning: '#FFF3E0',
    success: '#E8F5E9',
  }
  const bgColor = article.tipType ? tipColorMap[article.tipType] : undefined

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: bgColor,
        '&:hover': { boxShadow: 3 },
        transition: 'box-shadow 0.2s',
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Chip
            label={article.category}
            size="small"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          <Tooltip title={isBookmarked ? 'Remove bookmark' : 'Save article'}>
            <IconButton
              size="small"
              onClick={() => onToggleBookmark(article.slug)}
              aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark article'}
              sx={{ ml: 1, mt: -0.5 }}
            >
              {isBookmarked ? (
                <BookmarkIcon fontSize="small" sx={{ color: '#8B4513' }} />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
          {article.isVideo && <VideoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: '#DAA520' }} />}
          {article.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {article.content}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {article.tags.slice(0, 3).map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
          ))}
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, pt: 0, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          Was this helpful?
        </Typography>
        <Tooltip title="Yes, helpful">
          <span>
            <IconButton
              size="small"
              onClick={() => onFeedback(article.id, 'up')}
              disabled={!!feedbackGiven}
              aria-label="Mark as helpful"
              color={feedbackGiven === 'up' ? 'success' : 'default'}
            >
              <ThumbUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Not helpful">
          <span>
            <IconButton
              size="small"
              onClick={() => onFeedback(article.id, 'down')}
              disabled={!!feedbackGiven}
              aria-label="Mark as not helpful"
              color={feedbackGiven === 'down' ? 'error' : 'default'}
            >
              <ThumbDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {article.isVideo && (
          <Rating
            value={0}
            max={1}
            sx={{ ml: 1 }}
            aria-label={`Rate ${article.title}`}
          />
        )}
      </CardActions>
    </Card>
  )
}

export default HelpCenter
